import type { BlockDef } from './blocks'
import { Icon, type IconName } from '../components/Icon'

/** A glyph per block, so the palette is scannable rather than a wall of words. */
const ICONS: Record<string, IconName> = {
  p: 'text',
  h1: 'heading',
  h2: 'heading',
  h3: 'heading',
  h4: 'heading',
  ul: 'list',
  ol: 'list-ordered',
  checklist: 'check',
  quote: 'quote',
  code: 'code',
  hint: 'callout',
  expandable: 'expand',
  tabs: 'tabs',
  cards: 'cards',
  table: 'table',
  divider: 'divider',
  figure: 'image',
  image: 'image',
  file: 'page',
  button: 'link',
  codegroup: 'code',
}

/**
 * The rows shared by every block picker.
 *
 * Keeping the words, grouping and icons in one component means the gutter's
 * visible `+`, the slash command and any later picker cannot quietly become
 * three different descriptions of the same document model.
 */
export function BlockChoices({
  items,
  active = -1,
  optionRole = 'menuitem',
  onHover,
  onChoose,
}: {
  items: BlockDef[]
  active?: number
  optionRole?: 'menuitem' | 'option'
  onHover?: (index: number) => void
  onChoose: (block: BlockDef) => void
}) {
  return group(items).map(([label, choices]) => (
    <div key={label} className="blockchoices__group" role="group" aria-label={label}>
      <div className="slash__group" aria-hidden="true">
        {label}
      </div>
      {choices.map((block) => {
        const index = items.indexOf(block)
        return (
          <button
            key={block.id}
            className={`menu-item${index === active ? ' is-active' : ''}`}
            type="button"
            onMouseEnter={() => onHover?.(index)}
            onMouseDown={(event) => {
              // Preserve the editor selection the block will be inserted at.
              event.preventDefault()
            }}
            onClick={() => onChoose(block)}
            role={optionRole}
            {...(optionRole === 'option' ? { 'aria-selected': index === active } : {})}
          >
            <span className="menu-item__icon">
              <Icon name={ICONS[block.id] ?? 'blocks'} size={15} />
            </span>
            <span className="menu-item__body">
              <span className="menu-item__label">{block.label}</span>
              <span className="menu-item__hint">{block.hint}</span>
            </span>
          </button>
        )
      })}
    </div>
  ))
}

function group(items: BlockDef[]): [string, BlockDef[]][] {
  const out = new Map<string, BlockDef[]>()
  for (const item of items) out.set(item.group, [...(out.get(item.group) ?? []), item])
  return [...out.entries()]
}
